import { FC, useMemo } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

interface MoneyInChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
}

// Helper function to calculate trend line using linear regression
const calculateTrendLine = (data: number[]): number[] => {
  if (data.length <= 2) return data;
  
  // Use linear regression to calculate trend line
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = data.length;
  
  // Calculate sums for linear regression formula
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i];
    sumXY += i * data[i];
    sumXX += i * i;
  }
  
  // Calculate slope and y-intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  return data.map((_, i) => intercept + slope * i);
};

export const MoneyInChart: FC<MoneyInChartProps> = ({ 
  data, 
  showDebug,
  onExpandClick,
  monthsToShow = 24
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => {
    if (value === 0) return '$0';
    if (value % 1000 === 0) return `$${(value/1000)}k`;
    return `$${value.toLocaleString()}`;
  };
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Money In'];
  
  // Calculate maximum for Y-axis (20% above the max value, rounded to nearest $1000)
  const calculateYMax = useMemo(() => {
    if (data.length === 0) return 5000;
    
    const maxValue = Math.max(...data.map(item => item.money_in || 0));
    if (maxValue === 0) return 5000;
    
    // Add 20% buffer and round up to nearest $1000
    const withBuffer = maxValue * 1.2;
    return Math.ceil(withBuffer / 1000) * 1000;
  }, [data]);
  
  // Prepare chart datasets including the main line and trend line
  const getChartDatasets = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the most recent months up to monthsToShow
    const visibleData = sortedData.slice(-monthsToShow);
    
    // Extract money in values
    const values = visibleData.map(d => d.money_in || 0);
    
    // Calculate trend line
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Money In',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(59, 130, 246, 1)',
        pointBorderWidth: 2,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  }, [data, monthsToShow]);
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    // Calculate metrics for the overlay
    const currentMoneyIn = visibleData[visibleData.length - 1]?.money_in || 0;
    const previousMoneyIn = visibleData[visibleData.length - 2]?.money_in || 0;
    const monthlyChange = currentMoneyIn - previousMoneyIn;
    const percentChange = previousMoneyIn > 0 ? (monthlyChange / previousMoneyIn) * 100 : 0;
    
    // Determine the financial health status of money in
    const getMoneyInStatus = (change: number) => {
      if (change > 500) return { direction: 'up', label: 'Increasing', color: '#10B981' };
      if (change > 0) return { direction: 'up', label: 'Stable Growth', color: '#10B981' };
      if (change < -500) return { direction: 'down', label: 'Significant Drop', color: '#EF4444' };
      if (change < 0) return { direction: 'down', label: 'Decreasing', color: '#EF4444' };
      return { direction: 'stable', label: 'Stable', color: '#64748B' };
    };
    
    const status = getMoneyInStatus(monthlyChange);

    return (
      <div 
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-3">
          {/* Current Money In */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Current Monthly Income</div>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-brand-blue">
                ${currentMoneyIn.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              <div
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{ 
                  backgroundColor: `${status.color}15`,
                  color: status.color,
                  backdropFilter: 'blur(4px)'
                }}
              >
                {status.label}
              </div>
            </div>
          </div>
          
          {/* Monthly Change */}
          <div className="flex items-center justify-between gap-8 border-t border-slate-100 pt-2">
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Monthly Change</div>
              <div className="flex items-center gap-2">
                {monthlyChange === 0 ? (
                  <div className="text-sm font-medium">$0.00</div>
                ) : (
                  <>
                    <div
                      className="text-xl font-semibold"
                      style={{ color: monthlyChange >= 0 ? '#10B981' : '#EF4444' }}
                    >
                      {monthlyChange >= 0 ? '↑' : '↓'}
                    </div>
                    <div className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </>
                )}
              </div>
            </div>
            
            <div>
              <div className="text-xs text-slate-500 mb-0.5">% Change</div>
              <div className="text-sm font-medium">
                {percentChange === 0 ? '0%' : `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="h-full">
      <BaseChartTemplate
        data={data}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        title="Money In"
        dataKey="money_in"
        color="#3B82F6"
        yAxisFormatter={formatCurrency}
        tooltipFormatter={formatTooltip}
        renderOverlay={renderOverlay}
        isCurrentFinances={true}
        min={0}
        max={calculateYMax}
        pointRadius={3}
        pointBackgroundColor="rgba(255, 255, 255, 0.9)"
        pointBorderColor="rgba(59, 130, 246, 1)"
        pointBorderWidth={2}
        borderWidth={2.5}
        tension={LINE_STYLES.CURVED}
        customDatasets={getChartDatasets}
      />
    </div>
  );
};
